<template>
  <div class="header">
    <slot>
      <slot name="title">
        <h2 class="new-h2 d-flex align-center justify-start">
          {{ label }}
          <slot name="appendTitle" />
        </h2>
      </slot>
      <div class="d-flex align-center">
        <slot name="rightSide" />
        <slot name="download">
          <ChartDownload
            v-if="widgetDownloadId"
            class="ml-2"
            :download-id="widgetDownloadId"
            :include-csv-download-option="includeCsvDownloadOption"
            :file-name="fileName"
            :disabled="disabled"
            @download-csv="$emit('download-csv')"
          />
        </slot>
        <InsightsTooltip
          :tooltip="tooltip"
          :tooltip-placement="tooltipPlacement"
          class="ml-2 chart-header-toolip"
        />
      </div>
    </slot>
  </div>
</template>

<script setup>
import { useAppAuthData } from '@/stores/app/authData';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useTooltips } from '@/stores/insights/tooltips';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import ChartDownload from '@/components/insights/Chart/ChartDownload.vue';
import { toFileNameFormat } from '@/helpers/date';

const props = defineProps({
  label: { type: String, default: '' },
  widgetDownloadId: { type: String, default: '' },
  tooltipKey: { type: String, default: '' },
  tooltipPlacement: { type: String, default: 'left' },
  includeCsvDownloadOption: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

defineEmits('download-csv');

const { authData } = storeToRefs(useAppAuthData());
const { dateRange } = storeToRefs(useDatePicker());
const { tooltips } = storeToRefs(useTooltips());

const route = useRoute();

const tooltip = computed(() => {
  const { body_r: html, mode } = tooltips.value[props.tooltipKey] || {};

  return { html, mode, key: props.tooltipKey };
});

const fileName = computed(() => {
  let finalName = `${authData.value.projectName}-${route.name}-${props.widgetDownloadId}`;

  if (dateRange.value.from && dateRange.value.to) {
    finalName += `-${toFileNameFormat(dateRange.value.from)}-${toFileNameFormat(dateRange.value.to)}`;
  }

  return finalName;
});
</script>

<style lang="less" scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid @stroke-default;
  padding: 16px;
}

.chart-header-toolip {
  transform: translateY(-1px);
}
</style>
